import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { AlertTriangle, ArrowLeft, ArrowRight, CheckCircle2, FileText, FolderOpen, Image, Link2, Loader2, MapPin, RotateCcw, Settings2, Sparkles, Upload, X } from 'lucide-react';
import mammoth from 'mammoth';
import toast from 'react-hot-toast';
import { api } from '../../lib/api';
import { readWorkbookSheets, rowsToCsv } from '../../lib/excelWorkbook';
import { activeImportPhase, confidenceLabel, IMPORT_FLOW_STEPS, isDestinationEditable, operationLabel, resolveSourceHandling, storageModeAvailableForSources } from './documentImportUx';

type StorageMode = 'replicate' | 'external_link' | 'per_item';
type ImportMode = 'review' | 'assisted' | 'autonomous';

interface ImportSource {
  id: string;
  sourceType: 'upload' | 'external_link';
  fileName: string;
  mimeType?: string;
  text?: string;
  sourceUri?: string;
  file?: File;
  handlingMode?: 'replicate' | 'external_link';
}

interface DocumentImportWizardProps {
  isOpen: boolean;
  onClose: () => void;
}

const externalWarning = "Nexus can create a tile linking to this external document, but Nexus cannot control who can access the original file, whether the external file is moved or deleted, or whether permissions change in the external system. Confirm that the external document's sharing settings are appropriate before creating this tile.";
const UPLOAD_BATCH_SIZE = 20;

async function readFileAsText(file: File): Promise<string> {
  const extension = file.name.toLowerCase().split('.').pop();
  if (file.type.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension || '')) {
    return `${file.name}\n\nImage uploaded to Nexus media storage.`;
  }
  if (extension === 'docx') {
    const buffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer: buffer });
    return result.value;
  }
  if (extension === 'xlsx') {
    const sheets = await readWorkbookSheets(file);
    return sheets
      .map(({ name, rows }) => `# ${name}\n${rowsToCsv(rows)}`)
      .join('\n\n');
  }
  if (extension === 'pdf') {
    return `${file.name}\n\nPDF binary content was uploaded. Nexus will use file metadata for this import pass; full PDF text extraction can be connected on the server parser.`;
  }
  return await file.text();
}

export default function DocumentImportWizard({ isOpen, onClose }: DocumentImportWizardProps) {
  const queryClient = useQueryClient();
  const [step, setStep] = useState(1);
  const [sources, setSources] = useState<ImportSource[]>([]);
  const [urlValue, setUrlValue] = useState('');
  const [storageMode, setStorageMode] = useState<StorageMode>('replicate');
  const [mode, setMode] = useState<ImportMode>('assisted');
  const [wizardSettings, setWizardSettings] = useState({
    allowAutonomousWhenConfident: true,
    askBeforePlacement: false,
    askBeforeCreatingNestedPages: false,
    askBeforeCreatingRows: true,
    askBeforeCreatingColumns: true,
    askBeforeCreatingTags: false,
    askBeforeCreatingTaxonomyConcepts: true,
    askOnDuplicates: true,
    externalLinkAcknowledged: false,
  });
  const [isWorking, setIsWorking] = useState(false);
  const [job, setJob] = useState<any>(null);
  const [analysis, setAnalysis] = useState<any>(null);
  const [reviewDecisions, setReviewDecisions] = useState<Record<string, { action: 'approve' | 'skip'; targetPath?: string[]; targetPageId?: string }>>({});
  const [summary, setSummary] = useState<any>(null);
  const [importError, setImportError] = useState<string | null>(null);

  const { data: currentUser } = useQuery(
    ['current-user'],
    () => api.get('/users/me').then(res => res.data.data),
    { enabled: isOpen }
  );

  const { data: importSettings } = useQuery(
    ['organization-settings', currentUser?.organizationId, 'ai_import_settings'],
    () => api.get('/import-jobs/settings/effective').then((response) => response.data.data),
    { enabled: isOpen && !!currentUser?.organizationId, retry: false }
  );

  const { data: allPages = [] } = useQuery(
    'all-pages',
    () => api.get('/pages').then(res => res.data.data),
    { enabled: isOpen }
  );

  const sourceTypes = sources.map((source) => source.sourceType);
  const canReplicateSources = storageModeAvailableForSources('replicate', sourceTypes);
  const canLinkSources = storageModeAvailableForSources('external_link', sourceTypes);
  const canChoosePerItem = storageModeAvailableForSources('per_item', sourceTypes);
  const resolvedSourceHandling = sources.map((source) => resolveSourceHandling(storageMode, source));
  const willCreateExternalLinks = resolvedSourceHandling.includes('external_link');

  const modeAllowed = (candidate: ImportMode) => importSettings?.importModes?.[candidate] !== false;
  const policyAllowed = (key: string) => importSettings?.structuralCreation?.[key] !== 'never';
  const operations = analysis?.executionPlan?.operations || [];
  const unresolvedReviews = operations.filter((operation: any) => operation.reviewRequired && !reviewDecisions[operation.operationId]);

  const pageById = useMemo(() => new Map((allPages || []).map((page: any) => [page.id, page])), [allPages]);

  const getPagePath = (pageId?: string, visited = new Set<string>()): string[] => {
    if (!pageId || visited.has(pageId)) return [];
    visited.add(pageId);
    const page: any = pageById.get(pageId);
    if (!page) return [];
    return [...getPagePath(page.parentPageId, visited), page.title];
  };

  const findPageIdByPath = (path?: string[]) => {
    if (!path?.length) return undefined;
    const targetTitle = path[path.length - 1];
    const matchingPages = (allPages || []).filter((page: any) => page.title === targetTitle);
    if (matchingPages.length === 1) return matchingPages[0].id;
    return matchingPages.find((page: any) => getPagePath(page.id).join(' > ') === path.join(' > '))?.id;
  };

  const pageOptions = useMemo(() => (allPages || [])
    .map((page: any) => ({ id: page.id, path: getPagePath(page.id) }))
    .filter((page: any) => page.path.length)
    .sort((a: any, b: any) => a.path.join(' / ').localeCompare(b.path.join(' / '))), [allPages, pageById]);

  const updateReviewDecision = (operation: any, updates: Partial<{ action: 'approve' | 'skip'; targetPath: string[]; targetPageId: string }>) => {
    setReviewDecisions((current) => ({
      ...current,
      [operation.operationId]: {
        action: current[operation.operationId]?.action || 'approve',
        targetPath: current[operation.operationId]?.targetPath || operation.targetPath || [],
        targetPageId: current[operation.operationId]?.targetPageId || operation.targetPageId || findPageIdByPath(operation.targetPath),
        ...updates,
      },
    }));
  };

  const reviewReason = (operation: any) => {
    if (operation.type === 'document_parse_failed') return operation.payload?.reason || 'The document could not be parsed.';
    if (operation.confidence?.duplicate >= (importSettings?.confidenceThresholds?.duplicate || 0.8)) return 'A likely duplicate was found.';
    if (operation.confidence?.placement < (importSettings?.confidenceThresholds?.placement || 0.8)) return 'Placement confidence is below the required threshold.';
    if (operation.payload?.requiresExternalAcknowledgement) return 'External links require acknowledgement.';
    return 'A policy setting requires confirmation.';
  };

  const applyModePreset = (nextMode: ImportMode) => {
    setMode(nextMode);
    setWizardSettings((current) => ({
      ...current,
      allowAutonomousWhenConfident: nextMode !== 'review',
      askBeforePlacement: nextMode === 'review',
    }));
  };

  const changeDestination = (operation: any, pageId: string) => {
    updateReviewDecision(operation, {
      action: 'approve',
      targetPageId: pageId,
      targetPath: getPagePath(pageId),
    });

    // A different destination means the suggested page structure is no longer needed.
    operations
      .filter((candidate: any) => candidate.documentId === operation.documentId && ['create_nested_page', 'create_column_category'].includes(candidate.type))
      .forEach((candidate: any) => updateReviewDecision(candidate, { action: 'skip' }));
  };

  const readyDocumentCount = useMemo(() => {
    const includedDocumentIds = new Set<string>();
    operations.forEach((operation: any) => {
      const isSkipped = reviewDecisions[operation.operationId]?.action === 'skip' || ['skip_document', 'document_parse_failed'].includes(operation.type);
      if (!isSkipped && operation.documentId) includedDocumentIds.add(operation.documentId);
    });
    return includedDocumentIds.size;
  }, [operations, reviewDecisions]);

  const planItems = useMemo(() => {
    const documents = analysis?.documents || [];
    const grouped = new Map<string, any[]>();
    operations.forEach((operation: any) => {
      const key = operation.documentId || operation.operationId;
      grouped.set(key, [...(grouped.get(key) || []), operation]);
    });
    return Array.from(grouped.entries()).map(([documentId, documentOperations]) => {
      const document = documents.find((candidate: any) => candidate.id === documentId || candidate.documentId === documentId);
      const primary = documentOperations.find(isDestinationEditable)
        || documentOperations.find((operation: any) => ['send_to_knowledge_inbox', 'skip_document', 'document_parse_failed'].includes(operation.type))
        || documentOperations[documentOperations.length - 1];
      return {
        documentId,
        documentOperations,
        primary,
        title: document?.fileName || document?.name || primary?.payload?.title || 'Document',
      };
    });
  }, [analysis, operations]);

  const setPlanItemAction = (item: any, action: 'approve' | 'skip') => {
    const primaryDecision = item.primary ? reviewDecisions[item.primary.operationId] : undefined;
    const destinationChanged = primaryDecision?.targetPageId && primaryDecision.targetPageId !== item.primary?.targetPageId;
    item.documentOperations.forEach((operation: any) => {
      const isSuggestedStructure = ['create_nested_page', 'create_column_category'].includes(operation.type);
      const forcedAction = operation.type === 'document_parse_failed' || (action === 'approve' && destinationChanged && isSuggestedStructure) ? 'skip' : action;
      updateReviewDecision(operation, { action: forcedAction });
    });
  };

  useEffect(() => {
    if (!importSettings) return;
    if (!modeAllowed(mode)) {
      const fallback = (['assisted', 'review', 'autonomous'] as ImportMode[]).find(modeAllowed) || 'review';
      setMode(fallback);
    }
  }, [importSettings, mode]);

  useEffect(() => {
    const sourceModeIsInvalid =
      (storageMode === 'external_link' && !canLinkSources) ||
      (storageMode === 'per_item' && !canChoosePerItem);
    if (sourceModeIsInvalid) setStorageMode('replicate');
  }, [canChoosePerItem, canLinkSources, storageMode]);

  if (!isOpen) return null;

  const resetAndClose = () => {
    setStep(1);
    setSources([]);
    setUrlValue('');
    setStorageMode('replicate');
    setJob(null);
    setAnalysis(null);
    setSummary(null);
    setImportError(null);
    onClose();
  };

  const handleFiles = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setIsWorking(true);
    try {
      const parsed = await Promise.all(files.map(async (file) => ({
        id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
        sourceType: 'upload' as const,
        fileName: file.name,
        mimeType: file.type,
        file,
        text: await readFileAsText(file),
      })));
      setSources((current) => [...current, ...parsed]);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to read one or more files.');
    } finally {
      setIsWorking(false);
      event.target.value = '';
    }
  };

  const addUrl = () => {
    const trimmed = urlValue.trim();
    if (!trimmed) return;
    try {
      const parsedUrl = new URL(trimmed);
      if (!['http:', 'https:'].includes(parsedUrl.protocol)) throw new Error('Unsupported URL scheme');
    } catch {
      toast.error('Enter a complete http:// or https:// document URL.');
      return;
    }
    setSources((current) => [...current, {
      id: `${trimmed}-${Date.now()}`,
      sourceType: 'external_link',
      fileName: trimmed.replace(/^https?:\/\//, '').split('/')[0] || 'External document',
      sourceUri: trimmed,
      text: trimmed,
      handlingMode: 'external_link',
    }]);
    setUrlValue('');
  };

  const runImport = async () => {
    if (!sources.length) {
      toast.error('Add at least one document or link.');
      return;
    }
    if ((storageMode === 'external_link' && !canLinkSources) || (storageMode === 'per_item' && !canChoosePerItem)) {
      toast.error('The selected document handling option does not match the sources you added.');
      return;
    }
    if (willCreateExternalLinks && !wizardSettings.externalLinkAcknowledged) {
      toast.error('Acknowledge the external link warning before continuing.');
      return;
    }

    setIsWorking(true);
    setStep(3);
    setImportError(null);
    try {
      const createRes = await api.post('/import-jobs', {
        mode,
        storageMode,
        wizardSettings,
      });
      const createdJob = createRes.data.data;
      setJob(createdJob);

      const fileSources = sources.filter((source) => source.sourceType === 'upload' && source.file);
      const jsonSources = sources
        .filter((source) => source.sourceType !== 'upload' || !source.file)
        .map(({ file, ...source }) => ({
          ...source,
          handlingMode: resolveSourceHandling(storageMode, source),
        }));

      if (fileSources.length) {
        for (let index = 0; index < fileSources.length; index += UPLOAD_BATCH_SIZE) {
          const formData = new FormData();
          fileSources.slice(index, index + UPLOAD_BATCH_SIZE).forEach((source) => {
            if (source.file) formData.append('documents', source.file);
          });
          await api.post(`/import-jobs/${createdJob.id}/documents/upload`, formData);
        }
      }

      if (jsonSources.length) {
        await api.post(`/import-jobs/${createdJob.id}/documents`, { documents: jsonSources });
      }
      const analyseRes = await api.post(`/import-jobs/${createdJob.id}/analyse`);
      setAnalysis(analyseRes.data.data);
      setStep(4);
    } catch (error: any) {
      const message = error?.response?.data?.error || 'Import failed.';
      setImportError(message);
      toast.error(message);
      setStep(2);
    } finally {
      setIsWorking(false);
    }
  };

  const executeReviewedImport = async () => {
    if (!job?.id) return;
    const operationCount = analysis?.executionPlan?.operations?.length || 0;
    if (!operationCount) {
      toast.error('Analyse documents before executing the import.');
      return;
    }
    if (unresolvedReviews.length) {
      toast.error('Choose Include or Skip for every item that needs a decision.');
      return;
    }
    setIsWorking(true);
    try {
      if (Object.keys(reviewDecisions).length) {
        const decisionsRes = await api.patch(`/import-jobs/${job.id}/decisions`, { decisions: reviewDecisions });
        setAnalysis((current: any) => current ? {
          ...current,
          executionPlan: decisionsRes.data.data,
        } : current);
      }
      const executeRes = await api.post(`/import-jobs/${job.id}/execute`, {
        externalLinkAcknowledged: wizardSettings.externalLinkAcknowledged,
        forceReviewed: true,
      });
      setSummary(executeRes.data.data);
      setStep(5);
      queryClient.invalidateQueries(['page-init']);
      queryClient.invalidateQueries('all-pages');
      toast.success('Import completed.');
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Execution failed.');
    } finally {
      setIsWorking(false);
    }
  };

  const reanalyseImport = async () => {
    if (!job?.id) return;
    setIsWorking(true);
    try {
      const analyseRes = await api.post(`/import-jobs/${job.id}/analyse`);
      setAnalysis(analyseRes.data.data);
      setReviewDecisions({});
      toast.success('Import recommendations refreshed.');
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Re-analysis failed.');
    } finally {
      setIsWorking(false);
    }
  };

  const rollback = async () => {
    if (!job?.id) return;
    setIsWorking(true);
    try {
      await api.post(`/import-jobs/${job.id}/rollback`);
      toast.success('Import rollback completed.');
      queryClient.invalidateQueries(['page-init']);
      queryClient.invalidateQueries('all-pages');
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Rollback failed.');
    } finally {
      setIsWorking(false);
    }
  };

  const visiblePhase = activeImportPhase(step);

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-lg bg-white shadow-2xl" role="dialog" aria-modal="true" aria-labelledby="document-import-title">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="rounded-md bg-blue-50 p-2 text-blue-700"><Sparkles className="h-5 w-5" /></div>
            <div>
              <h2 id="document-import-title" className="text-lg font-semibold text-gray-900">Import Documents</h2>
              <p className="text-sm text-gray-500">{sources.length} item{sources.length === 1 ? '' : 's'} ready</p>
            </div>
          </div>
          <button onClick={resetAndClose} className="rounded-md p-2 text-gray-500 hover:bg-gray-100" aria-label="Close import wizard">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="border-b border-gray-200 px-5 py-4">
          <ol className="grid grid-cols-4 gap-3" aria-label="Import progress">
            {IMPORT_FLOW_STEPS.map((item, index) => {
              const phase = index + 1;
              const complete = visiblePhase > phase;
              const current = visiblePhase === phase;
              return (
                <li key={item.label} className="flex min-w-0 items-center gap-2">
                  <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${complete ? 'bg-green-600 text-white' : current ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500'}`}>
                    {complete ? <CheckCircle2 className="h-4 w-4" /> : phase}
                  </span>
                  <span className="min-w-0">
                    <span className={`block truncate text-sm font-medium ${current ? 'text-blue-700' : complete ? 'text-gray-900' : 'text-gray-500'}`}>{item.label}</span>
                    <span className="hidden truncate text-xs text-gray-500 sm:block">{item.description}</span>
                  </span>
                </li>
              );
            })}
          </ol>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
          {step === 1 && (
            <div className="space-y-5">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Choose documents to import</h3>
                <p className="mt-1 text-sm text-gray-600">Upload files or add links. You will review what Nexus plans to create and where it will go before the import starts.</p>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <label className="flex min-h-[150px] cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-6 text-center hover:border-blue-400 hover:bg-blue-50">
                  <Upload className="mb-3 h-8 w-8 text-blue-600" />
                  <span className="font-medium text-gray-900">Upload files</span>
                  <span className="mt-1 text-sm text-gray-500">PDF, DOCX, TXT, Markdown, CSV, XLSX, HTML, images</span>
                  <input className="hidden" type="file" multiple onChange={handleFiles} accept=".pdf,.docx,.txt,.md,.markdown,.csv,.xlsx,.html,.htm,.png,.jpg,.jpeg,.gif,.webp,.svg,image/*" />
                </label>
                <div className="rounded-lg border border-gray-200 bg-white p-5">
                  <div className="mb-3 flex items-center gap-2 text-gray-900">
                    <Link2 className="h-5 w-5 text-blue-600" />
                    <span className="font-medium">Add external link</span>
                  </div>
                  <div className="flex gap-2">
                    <input
                      value={urlValue}
                      onChange={(event) => setUrlValue(event.target.value)}
                      onKeyDown={(event) => event.key === 'Enter' && addUrl()}
                      placeholder="https://..."
                      className="min-w-0 flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    />
                    <button onClick={addUrl} className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">Add</button>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                {sources.map((source) => (
                  <div key={source.id} className="flex items-center justify-between rounded-md border border-gray-200 px-3 py-2">
                    <div className="flex min-w-0 items-center gap-3">
                      {source.sourceType === 'external_link' ? <Link2 className="h-4 w-4 text-gray-500" /> : source.mimeType?.startsWith('image/') ? <Image className="h-4 w-4 text-gray-500" /> : <FileText className="h-4 w-4 text-gray-500" />}
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-gray-900">{source.fileName}</p>
                        <p className="text-xs text-gray-500">{source.sourceType === 'external_link' ? source.sourceUri : source.mimeType || 'document'}</p>
                      </div>
                    </div>
                    <button onClick={() => setSources((current) => current.filter((item) => item.id !== source.id))} className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700" aria-label="Remove source">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-7">
              {importError && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="mt-0.5 h-5 w-5 text-red-600" />
                    <div>
                      <h3 className="font-medium text-red-900">Nexus could not build the import plan</h3>
                      <p className="mt-1 text-sm text-red-800">{importError}</p>
                      <p className="mt-2 text-xs text-red-700">Your documents have not been imported. Adjust the choices below and try again.</p>
                    </div>
                  </div>
                </div>
              )}

              <section>
                <div className="mb-3">
                  <h3 className="text-lg font-semibold text-gray-900">What should Nexus create?</h3>
                  <p className="mt-1 text-sm text-gray-600">This controls the result for each document. You can still change each document's destination on the next screen.</p>
                </div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  {[
                    ['per_item', 'Choose per item', 'Choose a duplicate or original-source link for each URL item. Uploaded files are always duplicated.', ''],
                    ['replicate', 'Create duplicates', 'Downloads readable URL content and creates editable Nexus content for every item. URL sources must be accessible without signing in.', 'Recommended'],
                    ['external_link', 'Link to originals', 'URL items become shortcuts to their source. Uploaded files are still duplicated.', ''],
                  ].map(([value, title, description, badge]) => {
                    const sourceCompatible = value === 'replicate'
                      ? canReplicateSources
                      : value === 'external_link'
                        ? canLinkSources
                        : canChoosePerItem;
                    const disabled = !sourceCompatible;
                    const selected = storageMode === value;
                    const sourceConstraint = value === 'per_item' && !canChoosePerItem
                      ? 'Available after you add at least one item by URL.'
                      : value === 'external_link' && !canLinkSources
                        ? 'Available after you add at least one item by URL.'
                        : description;
                    return (
                      <label key={value} className={`relative flex cursor-pointer flex-col rounded-lg border p-4 ${selected ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'} ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}>
                        <input type="radio" checked={selected} disabled={disabled} onChange={() => setStorageMode(value as StorageMode)} className="sr-only" />
                        <span className="flex items-start justify-between gap-2">
                          <span className="font-medium text-gray-900">{title}</span>
                          {badge && <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-medium text-blue-700">{badge}</span>}
                        </span>
                        <span className="mt-2 text-sm leading-5 text-gray-600">{sourceConstraint}</span>
                      </label>
                    );
                  })}
                </div>
              </section>

              {storageMode === 'per_item' && canChoosePerItem && (
                <section className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <div className="mb-3">
                    <h4 className="font-medium text-gray-900">Choose the result for each item</h4>
                    <p className="mt-1 text-sm text-gray-600">URL items can be copied into Nexus or left in their original system. Copying requires a URL that Nexus can access without signing in. Uploaded files must be copied.</p>
                  </div>
                  <div className="space-y-2">
                    {sources.map((source) => {
                      const isUrl = source.sourceType === 'external_link';
                      const handling = resolveSourceHandling('per_item', source);
                      return (
                        <div key={source.id} className="flex flex-col gap-3 rounded-md border border-gray-200 bg-white p-3 sm:flex-row sm:items-center sm:justify-between">
                          <div className="flex min-w-0 items-center gap-2">
                            {isUrl ? <Link2 className="h-4 w-4 shrink-0 text-blue-600" /> : <FileText className="h-4 w-4 shrink-0 text-gray-500" />}
                            <div className="min-w-0">
                              <p className="truncate text-sm font-medium text-gray-900">{source.fileName}</p>
                              <p className="truncate text-xs text-gray-500">{isUrl ? source.sourceUri : 'Uploaded file'}</p>
                            </div>
                          </div>
                          {isUrl ? (
                            <div className="flex shrink-0 rounded-md border border-gray-300 bg-gray-50 p-1" role="group" aria-label={`Import result for ${source.fileName}`}>
                              {([
                                ['replicate', 'Create duplicate'],
                                ['external_link', 'Link original'],
                              ] as const).map(([value, label]) => {
                                return (
                                  <button
                                    key={value}
                                    type="button"
                                    onClick={() => setSources((current) => current.map((item) => item.id === source.id ? { ...item, handlingMode: value } : item))}
                                    className={`rounded px-3 py-1.5 text-xs font-medium ${handling === value ? 'bg-white text-blue-700 shadow-sm ring-1 ring-gray-200' : 'text-gray-600 hover:text-gray-900'}`}
                                  >
                                    {label}
                                  </button>
                                );
                              })}
                            </div>
                          ) : (
                            <span className="shrink-0 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">Create duplicate</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}

              {willCreateExternalLinks && (
                <label className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
                  <input
                    type="checkbox"
                    checked={wizardSettings.externalLinkAcknowledged}
                    onChange={(event) => setWizardSettings((current) => ({ ...current, externalLinkAcknowledged: event.target.checked }))}
                    className="mt-1"
                  />
                  <span className="text-sm text-amber-900">{externalWarning}</span>
                </label>
              )}

              <section>
                <div className="mb-3">
                  <h3 className="text-lg font-semibold text-gray-900">How much review do you want?</h3>
                  <p className="mt-1 text-sm text-gray-600">The import plan is always shown before anything is created.</p>
                </div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  {[
                    ['assisted', 'Review exceptions', 'Nexus highlights uncertain placements, duplicates, and policy decisions.', 'Recommended'],
                    ['review', 'Review every item', 'You must explicitly include or skip every recommendation.', ''],
                    ['autonomous', 'Minimal prompts', 'Only blocked or high-risk items require a decision.', ''],
                  ].map(([value, title, description, badge]) => {
                    const selected = mode === value;
                    const disabled = !modeAllowed(value as ImportMode);
                    return (
                      <label key={value} className={`flex cursor-pointer flex-col rounded-lg border p-4 ${selected ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'} ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}>
                        <input type="radio" checked={selected} disabled={disabled} onChange={() => applyModePreset(value as ImportMode)} className="sr-only" />
                        <span className="flex items-start justify-between gap-2">
                          <span className="font-medium text-gray-900">{title}</span>
                          {badge && <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-medium text-blue-700">{badge}</span>}
                        </span>
                        <span className="mt-2 text-sm leading-5 text-gray-600">{description}</span>
                      </label>
                    );
                  })}
                </div>
              </section>

              <details className="rounded-lg border border-gray-200 bg-gray-50">
                <summary className="flex cursor-pointer list-none items-center gap-2 px-4 py-3 text-sm font-medium text-gray-800">
                  <Settings2 className="h-4 w-4 text-gray-500" />
                  Advanced review rules
                  <span className="font-normal text-gray-500">Fine-tune which changes require a decision</span>
                </summary>
                <div className="grid grid-cols-1 gap-3 border-t border-gray-200 p-4 md:grid-cols-2">
                  {[
                    ['askBeforeCreatingNestedPages', 'New nested pages', 'Ask before Nexus creates a new page below an existing page.'],
                    ['askBeforePlacement', 'Every document location', 'Ask you to include or skip every proposed destination.'],
                    ['askBeforeCreatingRows', 'New rows', 'Ask before adding rows to an existing structure.'],
                    ['askBeforeCreatingColumns', 'New page sections', 'Ask before adding a new section or column category.'],
                    ['askBeforeCreatingTags', 'New tags', 'Ask before creating tags that do not yet exist.'],
                    ['askBeforeCreatingTaxonomyConcepts', 'New taxonomy concepts', 'Ask before extending your organisation taxonomy.'],
                    ['askOnDuplicates', 'Possible duplicates', 'Ask when a similar document may already exist.'],
                  ].map(([key, label, description]) => {
                  const policyKeyByWizardKey: Record<string, string> = {
                    askBeforeCreatingNestedPages: 'nestedPages',
                    askBeforeCreatingRows: 'rows',
                    askBeforeCreatingColumns: 'columns',
                    askBeforeCreatingTags: 'tags',
                    askBeforeCreatingTaxonomyConcepts: 'taxonomyConcepts',
                  };
                  const requiredPolicy = policyKeyByWizardKey[key];
                  const disabled = requiredPolicy ? !policyAllowed(requiredPolicy) : false;
                  return (
                  <label key={key} className="flex items-start gap-3 rounded-md bg-white px-3 py-3">
                    <input
                      type="checkbox"
                      checked={(wizardSettings as any)[key]}
                      disabled={disabled}
                      onChange={(event) => setWizardSettings((current) => ({ ...current, [key]: event.target.checked }))}
                      className="mt-1"
                    />
                    <span>
                      <span className="block text-sm font-medium text-gray-800">{label}</span>
                      <span className="block text-xs leading-5 text-gray-500">{description}</span>
                    </span>
                  </label>
                )})}
                </div>
              </details>
            </div>
          )}

          {step === 3 && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Loader2 className="mb-4 h-10 w-10 animate-spin text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-900">Building your import plan</h3>
              <p className="mt-2 max-w-md text-sm text-gray-600">Nexus is reading your documents, checking for duplicates, and recommending a destination for each one. Nothing is being created yet.</p>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-5">
              <div className="flex flex-col gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <h3 className="font-semibold text-blue-950">Review where each document will go</h3>
                  <p className="mt-1 text-sm text-blue-800">Nothing has been created yet. Change any destination below, resolve highlighted decisions, then start the import.</p>
                </div>
                <div className="flex shrink-0 gap-4 text-center text-sm">
                  <div><span className="block text-xl font-semibold text-blue-950">{planItems.length}</span><span className="text-blue-700">Documents</span></div>
                  <div><span className="block text-xl font-semibold text-blue-950">{unresolvedReviews.length}</span><span className="text-blue-700">Decisions left</span></div>
                </div>
              </div>
              {!(analysis?.executionPlan?.operations || []).length && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="mt-0.5 h-5 w-5 text-red-600" />
                    <div>
                      <h3 className="font-medium text-red-900">Nexus could not create an import plan</h3>
                      <p className="text-sm text-red-800">Go back, check the documents and preferences, then build the plan again.</p>
                    </div>
                  </div>
                </div>
              )}
              {planItems.map((item: any) => {
                const operation = item.primary;
                if (!operation) return null;
                const decision = reviewDecisions[operation.operationId];
                const itemNeedsReview = item.documentOperations.some((candidate: any) => candidate.reviewRequired && !reviewDecisions[candidate.operationId]);
                const itemSkipped = item.documentOperations.every((candidate: any) => reviewDecisions[candidate.operationId]?.action === 'skip' || ['skip_document', 'document_parse_failed'].includes(candidate.type));
                const itemIncluded = item.documentOperations.some((candidate: any) => reviewDecisions[candidate.operationId]?.action === 'approve');
                const selectedPageId = decision?.targetPageId || operation.targetPageId || findPageIdByPath(operation.targetPath) || '';
                const inferredPath = selectedPageId ? getPagePath(selectedPageId) : [];
                const selectedPath = decision?.targetPath?.length ? decision.targetPath : inferredPath.length ? inferredPath : operation.targetPath || [];
                const supportingOperations = item.documentOperations.filter((candidate: any) => candidate.operationId !== operation.operationId);
                return (
                <article key={item.documentId} className={`rounded-lg border p-4 ${itemNeedsReview ? 'border-amber-300' : itemSkipped ? 'border-gray-200 bg-gray-50' : 'border-gray-200'}`}>
                  <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                    <div className="flex min-w-0 items-start gap-3">
                      <div className="rounded-md bg-gray-100 p-2 text-gray-600"><FileText className="h-5 w-5" /></div>
                      <div className="min-w-0">
                        <h4 className="truncate font-semibold text-gray-900">{item.title}</h4>
                        <p className="mt-0.5 text-sm text-gray-600">Nexus will create: <span className="font-medium text-gray-800">{operationLabel(operation.type)}</span></p>
                        <p className="mt-1 text-xs text-gray-500">{confidenceLabel(operation.confidence?.placement)} ({Math.round((operation.confidence?.placement || 0) * 100)}%)</p>
                      </div>
                    </div>
                    <span className={`self-start rounded-full px-2.5 py-1 text-xs font-medium ${itemNeedsReview ? 'bg-amber-100 text-amber-800' : itemSkipped ? 'bg-gray-200 text-gray-700' : 'bg-green-100 text-green-800'}`}>
                      {itemNeedsReview ? 'Decision needed' : itemSkipped ? 'Skipped' : itemIncluded ? 'Included' : 'Ready to import'}
                    </span>
                  </div>

                  {isDestinationEditable(operation) && (
                    <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50/60 p-3">
                      <div className="flex items-start gap-3">
                        <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-blue-700" />
                        <div className="min-w-0 flex-1">
                          <label htmlFor={`destination-${operation.operationId}`} className="block text-sm font-semibold text-blue-950">Destination</label>
                          <p className="mt-0.5 text-xs text-blue-700">Choose a different page here to move this document before importing.</p>
                          <select
                            id={`destination-${operation.operationId}`}
                            value={selectedPageId}
                            onChange={(event) => changeDestination(operation, event.target.value)}
                            className="mt-2 w-full rounded-md border border-blue-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                          >
                            {!selectedPageId && <option value="">{selectedPath.join(' / ') || 'Choose a page'}</option>}
                            {pageOptions.map((page: any) => <option key={page.id} value={page.id}>{page.path.join(' / ')}</option>)}
                          </select>
                        </div>
                      </div>
                    </div>
                  )}

                  {operation.reviewRequired && (
                    <div className="mt-3 flex items-start gap-2 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>{reviewReason(operation)}</span>
                    </div>
                  )}

                  {supportingOperations.length > 0 && (
                    <div className="mt-3 rounded-md bg-gray-50 px-3 py-2">
                      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Also planned</p>
                      <ul className="mt-1 space-y-1 text-sm text-gray-700">
                        {supportingOperations.map((candidate: any) => (
                          <li key={candidate.operationId} className="flex items-center justify-between gap-3">
                            <span>{operationLabel(candidate.type)}{candidate.targetPath?.length ? ` at ${candidate.targetPath.join(' / ')}` : ''}</span>
                            {candidate.reviewRequired && !reviewDecisions[candidate.operationId] && <span className="text-xs font-medium text-amber-700">Needs decision</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {(itemNeedsReview || itemIncluded || itemSkipped) && (
                    <div className="mt-4 flex justify-end gap-2">
                      {operation.type !== 'document_parse_failed' && operation.type !== 'skip_document' && (
                        <button
                          onClick={() => setPlanItemAction(item, 'approve')}
                          className={`rounded-md px-3 py-2 text-sm font-medium ${itemIncluded && !itemSkipped ? 'bg-green-600 text-white' : 'border border-green-300 text-green-700 hover:bg-green-50'}`}
                        >
                          Include document
                        </button>
                      )}
                        <button
                          onClick={() => setPlanItemAction(item, 'skip')}
                          className={`rounded-md px-3 py-2 text-sm font-medium ${itemSkipped ? 'bg-gray-700 text-white' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                        >
                          Skip document
                        </button>
                    </div>
                  )}
                </article>
              )})}
            </div>
          )}

          {step === 5 && (
            <div className="space-y-5">
              <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 text-green-600" />
                  <div>
                    <h3 className="font-medium text-green-900">Import complete</h3>
                    <p className="text-sm text-green-800">{summary?.documentsProcessed || 0} document{summary?.documentsProcessed === 1 ? '' : 's'} processed. The locations below show exactly where the new items were placed.</p>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
                {[
                  ['Content tiles', summary?.contentTilesCreated || 0],
                  ['Form tiles', summary?.formTilesCreated || 0],
                  ['External links', summary?.externalLinkTilesCreated || 0],
                  ['Nested pages', summary?.nestedPagesCreated || 0],
                  ['Knowledge Inbox', summary?.sentToKnowledgeInbox || 0],
                  ['Failures', summary?.failures || 0],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-lg border border-gray-200 p-4">
                    <p className="text-2xl font-semibold text-gray-900">{value}</p>
                    <p className="text-sm text-gray-500">{label}</p>
                  </div>
                ))}
              </div>
              <div className="space-y-2">
                {(summary?.created || []).map((item: any) => (
                  <div key={`${item.objectType}-${item.objectId}`} className="rounded-md border border-gray-200 px-3 py-2">
                    <p className="text-sm font-medium text-gray-900">{item.title || item.objectType}</p>
                    <p className="mt-1 flex items-center gap-1 text-xs text-gray-500"><FolderOpen className="h-3.5 w-3.5" />{(item.path || []).join(' / ') || 'Location unavailable'}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-gray-200 px-5 py-4">
          <button
            onClick={() => setStep((current) => current === 4 ? 2 : Math.max(1, current - 1))}
            disabled={step === 1 || step === 3 || step === 5 || isWorking}
            className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 disabled:opacity-40"
          >
            <ArrowLeft className="h-4 w-4" />
            {step === 4 ? 'Adjust preferences' : 'Back'}
          </button>
          <div className="flex items-center gap-2">
            {step === 5 && (
              <button onClick={rollback} disabled={isWorking} className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700">
                <RotateCcw className="h-4 w-4" />
                Rollback
              </button>
            )}
            {step === 1 && (
              <button
                onClick={() => setStep(2)}
                disabled={!sources.length}
                className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
              >
                Set import preferences
                <ArrowRight className="h-4 w-4" />
              </button>
            )}
            {step === 2 && (
              <button onClick={runImport} disabled={isWorking || !sources.length} className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-40">
                {isWorking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                Build import plan
              </button>
            )}
            {step === 4 && (
              <>
                <button onClick={reanalyseImport} disabled={isWorking || !job?.id} className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40">
                  {isWorking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  Rebuild plan
                </button>
                <button onClick={executeReviewedImport} disabled={isWorking || !operations.length || unresolvedReviews.length > 0 || readyDocumentCount === 0} className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-40">
                  {isWorking ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  Import {readyDocumentCount} document{readyDocumentCount === 1 ? '' : 's'}
                </button>
              </>
            )}
            {step === 5 && (
              <button onClick={resetAndClose} className="rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white">Done</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
